import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import Tooltip from '../../components/Tooltip';

function Users() {
  const [users, setUsers] = useState([]);
  const [practiceGroups, setPracticeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'practice_admin',
    assignments: [],
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ role: '', is_active: '' });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersRes, groupsRes] = await Promise.all([
        api.get('/users'),
        api.get('/practice-groups'),
      ]);
      setUsers(usersRes.data || []);
      setPracticeGroups(groupsRes.data || []);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const query = new URLSearchParams();
      if (filters.role) query.append('role', filters.role);
      if (filters.is_active) query.append('is_active', filters.is_active);
      const queryStr = query.toString() ? `?${query.toString()}` : '';
      const response = await api.get(`/users${queryStr}`);
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Validation
    const errors = [];
    if (!formData.email) errors.push('Email is required');
    if (!formData.firstName) errors.push('First name is required');
    if (!formData.lastName) errors.push('Last name is required');
    if (!editingUser && !formData.password) errors.push('Password is required');
    if (formData.role !== 'super_admin' && formData.assignments.length === 0) {
      errors.push('Non-super-admin users must have at least one group assignment');
    }

    if (errors.length > 0) {
      setFormError(errors.join(', '));
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        // Update user
        await api.put(`/users/${editingUser.id}`, {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          role: formData.role,
        });
        // Update assignments with per-group roles
        await api.put(`/assignments/user/${editingUser.id}`, {
          assignments: formData.assignments,
        });
      } else {
        // Create user with assignments
        await api.post('/users', {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          role: formData.role,
          assignments: formData.assignments,
        });
      }
      setShowForm(false);
      setEditingUser(null);
      resetForm();
      loadUsers();
    } catch (err) {
      setFormError(err.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      role: 'practice_admin',
      assignments: [],
    });
  };

  const handleEdit = async (user) => {
    try {
      // Load user's assignments with roles
      const assignmentsRes = await api.get(`/assignments/user/${user.id}`);
      const assignments = (assignmentsRes.data || []).map(a => ({
        practiceGroupId: a.practice_group_id,
        role: a.role || 'practice_admin',
      }));

      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        firstName: user.first_name,
        lastName: user.last_name,
        phone: user.phone || '',
        role: user.role,
        assignments,
      });
      setShowForm(true);
      setFormError('');
    } catch (err) {
      alert('Failed to load user details');
    }
  };

  const handleDeactivate = async (user) => {
    if (!window.confirm(`Are you sure you want to deactivate "${user.email}"?`)) {
      return;
    }

    try {
      await api.post(`/users/${user.id}/deactivate`);
      loadUsers();
    } catch (err) {
      alert(err.message || 'Failed to deactivate user');
    }
  };

  const handleActivate = async (user) => {
    try {
      await api.post(`/users/${user.id}/activate`);
      loadUsers();
    } catch (err) {
      alert(err.message || 'Failed to activate user');
    }
  };

  const handleResetPassword = async (user) => {
    const newPassword = window.prompt('Enter new password for ' + user.email);
    if (!newPassword) return;

    try {
      await api.post(`/users/${user.id}/reset-password`, { newPassword });
      alert('Password reset successfully');
    } catch (err) {
      alert(err.message || 'Failed to reset password');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingUser(null);
    resetForm();
    setFormError('');
  };

  const handleGroupToggle = (groupId) => {
    setFormData(prev => {
      const existing = prev.assignments.find(a => a.practiceGroupId === groupId);
      if (existing) {
        return { ...prev, assignments: prev.assignments.filter(a => a.practiceGroupId !== groupId) };
      } else {
        return { ...prev, assignments: [...prev.assignments, { practiceGroupId: groupId, role: prev.role }] };
      }
    });
  };

  const handleAssignmentRoleChange = (groupId, newRole) => {
    setFormData(prev => ({
      ...prev,
      assignments: prev.assignments.map(a =>
        a.practiceGroupId === groupId ? { ...a, role: newRole } : a
      ),
    }));
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div>
      <div className="card">
        <div style={styles.header}>
          <h2>User Management</h2>
          {!showForm && (
            <button
              className="button"
              onClick={() => {
                setShowForm(true);
                setEditingUser(null);
                resetForm();
              }}
            >
              + Add User
            </button>
          )}
        </div>

        {showForm && (
          <div style={styles.formContainer}>
            <h3>{editingUser ? 'Edit User' : 'New User'}</h3>
            {formError && <div className="error">{formError}</div>}
            <form onSubmit={handleSubmit}>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    style={styles.input}
                    disabled={saving}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    style={styles.input}
                    disabled={saving}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    style={styles.input}
                    disabled={saving}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Phone</label>
                  <input
                    type="tel"
                    placeholder="+1 555-123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={styles.input}
                    disabled={saving}
                  />
                  <small style={{ color: '#9ca3af', fontSize: 11 }}>Used for test mode SMS/call redirect</small>
                </div>
              </div>

              {!editingUser && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    style={styles.input}
                    placeholder="8+ chars, uppercase, lowercase, number"
                    disabled={saving}
                  />
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  style={styles.select}
                  disabled={saving}
                >
                  <option value="practice_admin">Practice Admin - Full practice management</option>
                  <option value="clinical_admin">Clinical Admin - Clinical workflows + read-only business</option>
                  <option value="care_provider">Care Provider - Own patients & encounters only</option>
                  <option value="super_admin">Super Admin - Platform administrator</option>
                </select>
              </div>

              {formData.role !== 'super_admin' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Practice Group Assignments *</label>
                  <div style={styles.checkboxGroup}>
                    {practiceGroups.filter(g => g.is_active).map(group => {
                      const assignment = formData.assignments.find(a => a.practiceGroupId === group.id);
                      return (
                        <div key={group.id} style={styles.assignmentRow}>
                          <label style={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={!!assignment}
                              onChange={() => handleGroupToggle(group.id)}
                              disabled={saving}
                            />
                            {group.name}
                          </label>
                          {assignment && (
                            <select
                              value={assignment.role}
                              onChange={(e) => handleAssignmentRoleChange(group.id, e.target.value)}
                              style={styles.roleSelect}
                              disabled={saving}
                            >
                              <option value="practice_admin">Practice Admin</option>
                              <option value="clinical_admin">Clinical Admin</option>
                              <option value="care_provider">Care Provider</option>
                            </select>
                          )}
                        </div>
                      );
                    })}
                    {practiceGroups.filter(g => g.is_active).length === 0 && (
                      <span style={{ color: '#666' }}>No active practice groups available</span>
                    )}
                  </div>
                </div>
              )}

              <div style={styles.formActions}>
                <button type="submit" className="button" disabled={saving}>
                  {saving ? 'Saving...' : editingUser ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  className="button secondary"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="filters" style={{ marginBottom: '20px' }}>
          <select
            value={filters.role}
            onChange={(e) => setFilters({ ...filters, role: e.target.value })}
          >
            <option value="">All Roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="practice_admin">Practice Admin</option>
            <option value="clinical_admin">Clinical Admin</option>
            <option value="care_provider">Care Provider</option>
          </select>
          <select
            value={filters.is_active}
            onChange={(e) => setFilters({ ...filters, is_active: e.target.value })}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
        </div>

        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role <Tooltip text="Super Admin: Full access. Practice Admin: Limited to assigned groups" /></th>
              <th>Status <Tooltip text="Active: Can login. Inactive: Access disabled" /></th>
              <th>Last Login <Tooltip text="Most recent successful login date" /></th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#666' }}>
                  No users found.
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td><strong>{user.first_name} {user.last_name}</strong></td>
                  <td>{user.email}</td>
                  <td style={{ color: user.phone ? '#111827' : '#d1d5db', fontSize: 13 }}>{user.phone || '—'}</td>
                  <td>
                    <span className={`badge ${
                      user.role === 'super_admin' ? 'info' :
                      user.role === 'practice_admin' ? 'warning' :
                      user.role === 'clinical_admin' ? 'success' : 'secondary'
                    }`}>
                      {{ super_admin: 'Super Admin', practice_admin: 'Practice Admin', clinical_admin: 'Clinical Admin', care_provider: 'Care Provider' }[user.role] || user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${user.is_active ? 'success' : 'danger'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {user.last_login_at
                      ? new Date(user.last_login_at).toLocaleString()
                      : 'Never'}
                  </td>
                  <td>
                    <div style={styles.actions}>
                      <button
                        style={styles.actionButton}
                        onClick={() => handleEdit(user)}
                        title="Edit"
                      >
                        Edit
                      </button>
                      <button
                        style={styles.actionButton}
                        onClick={() => handleResetPassword(user)}
                        title="Reset Password"
                      >
                        Reset PW
                      </button>
                      {user.is_active ? (
                        <button
                          style={{ ...styles.actionButton, ...styles.dangerButton }}
                          onClick={() => handleDeactivate(user)}
                          title="Deactivate"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          style={{ ...styles.actionButton, ...styles.successButton }}
                          onClick={() => handleActivate(user)}
                          title="Activate"
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  formContainer: {
    background: '#f8f9fa',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  formRow: {
    display: 'flex',
    gap: '16px',
  },
  formGroup: {
    marginBottom: '16px',
    flex: 1,
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    background: '#fff',
    border: '1px solid #ddd',
    borderRadius: '4px',
    maxHeight: '200px',
    overflow: 'auto',
  },
  assignmentRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    flex: 1,
    minWidth: 0,
  },
  roleSelect: {
    padding: '4px 8px',
    fontSize: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    background: '#fff',
    flexShrink: 0,
  },
  formActions: {
    display: 'flex',
    gap: '10px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  actionButton: {
    padding: '6px 10px',
    fontSize: '12px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    background: '#667eea',
    color: '#fff',
  },
  dangerButton: {
    background: '#dc3545',
  },
  successButton: {
    background: '#28a745',
  },
};

export default Users;
